import { useParams, Link } from "react-router-dom";
import useReveal from "../../components/useReveal";

const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const members = {
  "sviatoslav-gladyshev": {
    name: "Sviatoslav Gladyshev",
    role: "Chief Executive Officer",
    roleShort: "CEO",
    gradient: "linear-gradient(135deg, #6e40c9 0%, #c940a0 100%)",
    photo: "https://firebasestorage.googleapis.com/v0/b/oyola-ai.firebasestorage.app/o/sviatoslav_gladyshev.jpg?alt=media&token=09dace96-e2fd-4964-98e1-283507b4b8f0",
    bio: [
      "Sviatoslav Gladyshev is Co-Founder of Ateno, building hardware for 3D Gaussian Splatting.",
      "He writes about semiconductor technology and the physical constraints shaping AI infrastructure.",
    ],
    links: [
      { label: "LinkedIn", href: "https://www.linkedin.com/in/sviatoslavgl/", icon: <LinkedInIcon /> },
      { label: "X", href: "https://x.com/sviatoslavg?s=21", icon: <XIcon /> },
    ],
  },
  "artemii-mavlitov": {
    name: "Artemii Mavlitov",
    role: "Chief Technology Officer",
    roleShort: "CTO",
    gradient: "linear-gradient(135deg, #1a7fe8 0%, #00c2b3 100%)",
    photo: "https://firebasestorage.googleapis.com/v0/b/oyola-ai.firebasestorage.app/o/artemii_mavlitov.jpg?alt=media&token=e7e2b868-0d65-447c-a27c-8fce152e93c1",
    bio: [
      "Artemii leads Ateno's engineering across the reconstruction stack and the infrastructure that powers the company’s spatial products.",
      "He brings a deep background in computer vision and systems programming, with a focus on making geometry-heavy workflows fast, reliable, and production-ready.",
      "His view is simple: the best spatial technology disappears into the product experience and leaves the user with clear, useful output.",
    ],
    links: [
      { label: "LinkedIn", href: "https://linkedin.com/in/artemii-mavlitov", icon: <LinkedInIcon /> },
    ],
  },
};

export default function TeamMemberPage() {
  useReveal();
  const { slug } = useParams();
  const member = members[slug];

  if (!member) {
    return (
      <div style={{ background: "var(--bg-primary)", minHeight: "100vh", paddingTop: 120, textAlign: "center" }}>
        <h1 style={{ fontSize: 32, color: "var(--text-primary)" }}>Person not found.</h1>
        <Link to="/company" style={{ color: "var(--text-tertiary)", fontSize: 14 }}>← Back to Company</Link>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--bg-primary)", paddingTop: 80 }}>
      <section className="section section-dark">
        <div className="container-narrow">
          {/* Back link */}
          <Link
            to="/company"
            className="reveal"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 13, color: "var(--text-tertiary)", textDecoration: "none",
              marginBottom: 40, letterSpacing: "-.01em",
              transition: "color .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--text-primary)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--text-tertiary)"}
          >
            ← Company
          </Link>

          {/* Hero row */}
          <div className="reveal" style={{ display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 52 }}>
            {/* Photo */}
            <div style={{
              width: 160, height: 160, borderRadius: 20, overflow: "hidden", flexShrink: 0,
              background: member.gradient,
            }}>
              {member.photo ? (
                <img
                  src={member.photo}
                  alt={member.name}
                  style={{
                    width: "120%", height: "120%",
                    objectFit: "cover",
                    objectPosition: "center center",
                    marginLeft: "-10%", marginTop: "-10%",
                    display: "block",
                  }}
                />
              ) : (
                <div style={{
                  width: "100%", height: "100%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 48, fontWeight: 700, color: "#fff",
                }}>
                  {member.name.split(" ").map(w => w[0]).join("")}
                </div>
              )}
            </div>

            {/* Name block */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>{member.roleShort}</div>
              <h1 className="headline" style={{ marginBottom: 12, fontSize: 36 }}>{member.name}</h1>
              <div style={{ fontSize: 14, color: "var(--text-tertiary)", letterSpacing: "-.01em" }}>{member.role}</div>

              {/* Personal social links with icons */}
              {member.links?.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                  {member.links.map((l, i) => (
                    <a
                      key={i}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={l.label}
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
                        padding: "8px 14px", borderRadius: 980,
                        border: "1px solid var(--border-color)",
                        background: "transparent",
                        color: "var(--text-secondary)",
                        textDecoration: "none", fontSize: 13, letterSpacing: "-.01em",
                        transition: "border-color .15s, color .15s, background .15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--text-primary)"; e.currentTarget.style.color = "var(--text-primary)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-color)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
                    >
                      {l.icon}
                      {l.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bio paragraphs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {member.bio.map((para, i) => (
              <p key={i} className="reveal" style={{ fontSize: 18, color: "var(--text-secondary)", lineHeight: 1.7, margin: 0 }}>
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
